//
import { SystemSignalRService } from "../services/data-services/SystemSignalRService";
import { convertFieldSystemsToSystemMarker } from "pages/Map/components/data/SystemDataConverter";
import { NotificationsStore } from "./NotificationsStore";
import { IFieldSystem, ISystemData } from "pages/Map/components/interfaces";
import { MarkerType } from "pages/Map/components/enums";
import { action, computed, makeObservable, observable } from "mobx";
import { MarkerSeverity } from "pages/Map/components/enums";

export class SystemStore {
  tscUrl: string;
  selectedTypes: MarkerType[] = [MarkerType.BASEBALL, MarkerType.SOFTBALL];
  isLoading: boolean = false;

  systems: ISystemData[] = [];
  lastUpdatedDate: Date = new Date();

  private GOOGLE_API_URL: string = "";
  googleApiUrl: string = this.GOOGLE_API_URL;

  constructor(
    notificationStore: NotificationsStore,
    systemSignalRService: SystemSignalRService,
    googleApiUrl: string,
    tscUrl: string
  ) {
    // super(notificationStore, systemSignalRService, googleApiUrl);
    this.tscUrl = tscUrl;
    this.googleApiUrl = googleApiUrl;

    systemSignalRService.subscribeBaseballEventHandlers(
      this.onSystemUpdateReceived,
      this.onNewSystemReceived,
      this.onDeleteSystemReceived
    );

    makeObservable(this, {
      selectedTypes: observable,
      isLoading: observable,
      systems: observable,
      lastUpdatedDate: observable,
      googleApiUrl: observable,

      filteredSystems: computed,
      all: computed,
      normals: computed,
      warnings: computed,
      criticals: computed,

      setIsLoading: action,
      onNewSystems: action,
      onSystemUpdate: action,
      onDeleteSystems: action,
    });
  }

  get filteredSystems() {
    return this.systems.filter((system) =>
      this.selectedTypes.includes(system.type)
    );
  }

  get all() {
    return this.filteredSystems;
  }
  get normals() {
    return this.filteredSystems.filter(
      (x) => x.status.statusType === MarkerSeverity.Normal
    );
  }
  get warnings() {
    return this.filteredSystems.filter(
      (x) => x.status.statusType === MarkerSeverity.Warning
    );
  }
  get criticals() {
    return this.filteredSystems.filter(
      (x) => x.status.statusType === MarkerSeverity.Critical
    );
  }

  private onSystemUpdateReceived: (props: any) => void = (
    fieldSystems: IFieldSystem[]
  ) => {
    var systemsData: ISystemData[] = convertFieldSystemsToSystemMarker(
      fieldSystems,
      this.tscUrl
    );

    this.onSystemUpdate(systemsData);
  };

  private onNewSystemReceived: (props: any) => void = (
    fieldSystems: IFieldSystem[]
  ) => {
    var systemsData: ISystemData[] = convertFieldSystemsToSystemMarker(
      fieldSystems,
      this.tscUrl
    );

    this.onNewSystems(systemsData);
  };

  private onDeleteSystemReceived: (props: any) => void = (
    fieldSystemIDs: string[]
  ) => {
    this.onDeleteSystems(fieldSystemIDs);
  };

  public setSelectedTypes = (selectedTypes: MarkerType[]) => {
    this.selectedTypes = selectedTypes;
  };

  public setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  // ===========================================
  onNewSystems: (systemsData: ISystemData[]) => void = (
    systemsData: ISystemData[]
  ) => {
    var newSystems = [...this.systems];

    // // FOR DEV ONLY
    // systemsData.forEach((systemData: ISystemData) => {
    //   if (systemData.status.statusType === MarkerSeverity.Unknown) {
    //     systemData.status.statusType = MarkerSeverity.Normal;
    //   } else if (systemData.status.statusType === MarkerSeverity.Normal) {
    //     systemData.status.statusType = MarkerSeverity.Warning;
    //   } else if (systemData.status.statusType === MarkerSeverity.Warning) {
    //     systemData.status.statusType = MarkerSeverity.Critical;
    //   } else {
    //     systemData.status.statusType = MarkerSeverity.Unknown;
    //   }
    // });
    // systemsData[1].status.statusType = MarkerSeverity.Critical;
    // systemsData[2].status.statusType = MarkerSeverity.Critical;

    // // go through system from 10 to 50 and change type to be softball
    // for (let i = 10; i < 50; i++) {
    //   systemsData[i].type = MarkerType.SOFTBALL;
    //   // change status reandomly
    //   if (i % 2 === 0) {
    //     systemsData[i].status.statusType = MarkerSeverity.Normal;
    //   } else {
    //     systemsData[i].status.statusType = MarkerSeverity.Warning;
    //   }

    //   systemsData[20].status.statusType = MarkerSeverity.Critical;
    // }

    systemsData.forEach((systemData: ISystemData) => {
      if (!this.systems.find((s: ISystemData) => s.id === systemData.id))
        newSystems.push(systemData);
    });

    this.systems = newSystems;
    this.lastUpdatedDate = new Date();
  };

  onSystemUpdate: (systemsData: ISystemData[]) => void = (
    systemsData: ISystemData[]
  ) => {
    // console.log('Update Received for ' + systemsData[0]?.type)

    var updatedSystems = [...this.systems];
    systemsData.forEach((systemData: ISystemData) => {
      var oldSystem = updatedSystems.find((oldSystemData: ISystemData) => {
        return oldSystemData.id === systemData.id;
      });
      if (oldSystem !== undefined) {
        var oldSystemIndex = updatedSystems.indexOf(oldSystem);

        const isSeverityChanged =
          oldSystem.status.statusType !== systemData.status.statusType;
        const isNameChanged = oldSystem.name !== systemData.name;
        const isLatLngChanged =
          oldSystem.position.lat !== systemData.position.lat ||
          oldSystem.position.lng !== systemData.position.lng;

        if (isSeverityChanged || isNameChanged || isLatLngChanged)
          systemData.status.needRerender = true;

        updatedSystems[oldSystemIndex] = systemData;
      } else {
        updatedSystems.push(systemData);
      }
    });
    this.systems = updatedSystems;
    this.lastUpdatedDate = new Date();
  };

  onDeleteSystems: (systemIDs: string[]) => void = (systemIDs: string[]) => {
    systemIDs.forEach((systemID) => {
      this.systems = this.systems.filter((system: ISystemData) => {
        return system.position.GroupID !== systemID;
      });
    });
    this.lastUpdatedDate = new Date();
  };
}
